import { useQuery, useMutation, UseQueryOptions, UseMutationOptions, QueryKey } from '@tanstack/react-query'
import { graphqlRequest } from './graphqlClient'
import { queryClient } from './queryClient'

/**
 * Generic hook for GraphQL queries using TanStack Query
 *
 * @param queryKey - Unique key for the query (for caching)
 * @param document - GraphQL query document string
 * @param variables - Variables for the GraphQL query
 * @param options - Additional TanStack Query options
 *
 * @example
 * const { data, isLoading, error } = useGraphQLQuery(
 *   queryKeys.dashboard.pending,
 *   GET_PENDING_LEAVES_QUERY,
 *   { status: 'PENDING' }
 * )
 */
export function useGraphQLQuery<TData = unknown, TError = Error>(
  queryKey: QueryKey,
  document: string,
  variables?: Record<string, unknown>,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TData, TError>({
    queryKey: variables ? [...(queryKey as unknown[]), variables] : queryKey,
    queryFn: async () => {
      const data = await graphqlRequest<TData>(document, variables)
      return data
    },
    ...options,
  })
}

/**
 * Generic hook for GraphQL mutations using TanStack Query
 *
 * @param document - GraphQL mutation document string
 * @param options - Additional TanStack Query mutation options
 *
 * @example
 * const { mutate, isLoading } = useGraphQLMutation(
 *   APPROVE_LEAVE_MUTATION,
 *   {
 *     onSuccess: () => {
 *       // Invalidate related queries
 *       queryClient.invalidateQueries(queryKeys.dashboard.pending)
 *     }
 *   }
 * )
 *
 * // Later in your component:
 * mutate({ leaveId: '123' })
 */
export function useGraphQLMutation<TData = unknown, TVariables = Record<string, unknown>, TError = Error>(
  document: string,
  options?: UseMutationOptions<TData, TError, TVariables>
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      return graphqlRequest<TData>(document, variables as Record<string, unknown>)
    },
    ...options,
  })
}

/**
 * Hook to manually invalidate queries
 *
 * @example
 * const invalidate = useInvalidateQueries()
 * invalidate(queryKeys.dashboard.all)
 */
export function useInvalidateQueries() {
  return (queryKey: QueryKey) => {
    return queryClient.invalidateQueries({ queryKey })
  }
}

/**
 * Hook to manually refetch queries
 *
 * @example
 * const refetch = useRefetchQueries()
 * refetch(queryKeys.dashboard.pending)
 */
export function useRefetchQueries() {
  return (queryKey: QueryKey) => {
    return queryClient.refetchQueries({ queryKey })
  }
}

/**
 * Hook to manually set query data (for optimistic updates)
 *
 * @example
 * const setQueryData = useSetQueryData()
 * setQueryData(queryKeys.dashboard.pending, (oldData) => ({
 *   ...oldData,
 *   leaves: oldData.leaves.filter(l => l.id !== leaveId)
 * }))
 */
export function useSetQueryData() {
  return <TData = unknown>(queryKey: QueryKey, updater: TData | ((oldData: TData | undefined) => TData)) => {
    return queryClient.setQueryData<TData>(queryKey, updater)
  }
}
